import { Box, Paper, Typography, IconButton, Tooltip } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuDownload, LuFileText } from "react-icons/lu";
import AgGrid from "../components/grid/AgGrid";
import AddButton from "../components/button/AddButton";
import RemoveButton from "../components/button/RemoveButton";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../components/container/TableHeaderToolsContainer";
import { defaultColDef, leftAlignColDef, rightAlignColDef } from "../components/grid/columnDefinitions/columnDefinitions";
import config from "../config";

const ACCEPTED_TYPES = ".pdf,.csv,.xlsx,.doc,.docx,.jpg,.jpeg,.png";
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const StatusRenderer = (params) => {
  const { t } = useTranslation();
  const { isUploading, isDerived } = params.data;

  if (isDerived) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: "5px",
          px: 1.5, py: "5px", borderRadius: "20px",
          bgcolor: "rgba(100, 116, 139, 0.10)",
          border: "1px solid rgba(100, 116, 139, 0.28)",
        }}>
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#64748b", flexShrink: 0 }} />
          <Box component="span" sx={{ fontSize: "12.5px", fontWeight: 600, color: "#475569", lineHeight: 1 }}>
            {t("Linked from Certificates")}
          </Box>
        </Box>
      </Box>
    );
  }

  if (isUploading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: "5px",
          px: 1.5, py: "5px", borderRadius: "20px",
          bgcolor: "rgba(59, 130, 246, 0.10)",
          border: "1px solid rgba(59, 130, 246, 0.28)",
          "@keyframes uploading-pulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.45 },
          },
          animation: "uploading-pulse 1.2s ease-in-out infinite",
        }}>
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#3b82f6", flexShrink: 0 }} />
          <Box component="span" sx={{ fontSize: "12.5px", fontWeight: 600, color: "#1d4ed8", lineHeight: 1 }}>
            {t("Uploading...")}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
      <Box sx={{
        display: "inline-flex", alignItems: "center", gap: "5px",
        px: 1.5, py: "5px", borderRadius: "20px",
        bgcolor: "rgba(101, 157, 51, 0.10)",
        border: "1px solid rgba(101, 157, 51, 0.28)",
      }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#659d33", flexShrink: 0 }} />
        <Box component="span" sx={{ fontSize: "12.5px", fontWeight: 600, color: "#4a7c23", lineHeight: 1 }}>
          {t("Successfully Uploaded")}
        </Box>
      </Box>
    </Box>
  );
};

const FileNameRenderer = (params) => {
  if (params.data?.isUploading) return <Box sx={{ height: "100%", width: "100%" }} />;
  const isDerived = params.data?.isDerived;

  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
      <Box
        onClick={isDerived ? () => window.open(params.data.url, "_blank") : undefined}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          maxWidth: "100%",
          px: 1.5,
          py: "5px",
          borderRadius: "20px",
          bgcolor: "rgba(217, 119, 6, 0.10)",
          border: "1px solid rgba(217, 119, 6, 0.28)",
          cursor: isDerived ? "pointer" : "default",
        }}
      >
        <LuFileText size={14} style={{ color: "#d97706", flexShrink: 0 }} />
        <Box
          component="span"
          sx={{
            fontSize: "12.5px",
            fontWeight: 600,
            color: "#92400e",
            lineHeight: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textDecoration: isDerived ? "underline" : "none",
          }}
        >
          {params.value}
        </Box>
      </Box>
    </Box>
  );
};

const formatUploadedAt = (uploadedAt) => {
  if (!uploadedAt) return "";
  try {
    const dateStr = (uploadedAt.endsWith("Z") || uploadedAt.includes("+") || (uploadedAt.includes("-") && uploadedAt.includes("T")))
      ? uploadedAt
      : uploadedAt.includes("T")
        ? `${uploadedAt}Z`
        : `${uploadedAt.replace(" ", "T")}Z`;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return uploadedAt;
    }
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (e) {
    return uploadedAt;
  }
};

function EvidenceRepository({ entityId, idColumnName, scope = "Evidence", extraRows = [] }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [uploadingIds, setUploadingIds] = useState(new Set());

  useEffect(() => {
    if (!entityId) { setDocuments([]); return; }
    fetch(`${config.apiUrl}/api/fetch_emission_reference_files?ID=${entityId}`)
      .then((res) => res.json())
      .then((data) => setDocuments(data.filter((f) => f.scope === scope).map((f) => ({ id: f.id, name: f.name, url: f.url, scope: f.scope, uploaded_at: f.uploaded_at }))))
      .catch(() => {});
  }, [entityId, scope]);

  const handleFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = null;
    if (!files.length || !entityId) return;

    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) return;

    const tempIds = files.map((_, i) => `uploading-${Date.now()}-${i}`);
    setUploadingIds((prev) => { const s = new Set(prev); tempIds.forEach((id) => s.add(id)); return s; });

    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      formData.append("rowID", entityId);
      formData.append("scope", scope);
      formData.append("IDColumnName", idColumnName);

      const res = await fetch(`${config.apiUrl}/api/upload_emission_reference_files`, { method: "POST", body: formData });
      if (!res.ok) throw new Error("Upload failed");

      const refreshed = await fetch(`${config.apiUrl}/api/fetch_emission_reference_files?ID=${entityId}`);
      const data = await refreshed.json();
      setDocuments(data.filter((f) => f.scope === scope).map((f) => ({ id: f.id, name: f.name, url: f.url, scope: f.scope, uploaded_at: f.uploaded_at })));
    } catch (err) {
      console.error("[EvidenceRepository] Upload error:", err);
    } finally {
      setUploadingIds((prev) => { const s = new Set(prev); tempIds.forEach((id) => s.delete(id)); return s; });
    }
  }, [entityId, idColumnName, scope]);

  const handleDelete = useCallback(async () => {
    if (!selectedIds.size) return;
    const toDelete = documents.filter((d) => selectedIds.has(d.id));
    const file_ids = toDelete.map((d) => d.id);
    const file_urls = toDelete.map((d) => d.url);
    try {
      await fetch(`${config.apiUrl}/api/delete_emission_reference_files`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_ids, file_urls }),
      });
      setDocuments((prev) => prev.filter((d) => !selectedIds.has(d.id)));
      setSelectedIds(new Set());
    } catch (err) {
      console.error("[EvidenceRepository] Delete error:", err);
    }
  }, [selectedIds, documents]);

  const handleDownload = useCallback(() => {
    documents.filter((d) => selectedIds.has(d.id)).forEach((doc) => {
      window.open(doc.url, "_blank");
    });
  }, [selectedIds, documents]);

  const rowData = useMemo(() => [
    ...documents.map((doc) => ({ ...doc, isUploading: false })),
    ...[...uploadingIds].map((uid) => ({
      id: uid, name: "", url: "", scope, isUploading: true,
    })),
    ...extraRows.map((row) => ({ ...row, isUploading: false, isDerived: true })),
  ], [documents, uploadingIds, scope, extraRows]);

  const columnDefs = useMemo(() => [
    {
      headerName: "Sl.No",
      valueGetter: (params) => params.node.rowIndex + 1,
      width: 58,
      minWidth: 48,
      maxWidth: 80,
      ...defaultColDef,
      ...rightAlignColDef,
      suppressMovable: false,
      sortable: false,
      initialFlex: undefined,
    },
    {
      headerName: "Document Name",
      field: "name",
      flex: 2,
      ...defaultColDef,
      ...leftAlignColDef,
      cellRenderer: FileNameRenderer,
      headerCheckboxSelection: true,
      checkboxSelection: false,
      suppressMovable: false,
    },
    {
      headerName: "Upload Date & Time",
      field: "uploaded_at",
      flex: 1.5,
      ...defaultColDef,
      ...leftAlignColDef,
      valueFormatter: (params) => formatUploadedAt(params.value),
      suppressMovable: false,
    },
    {
      headerName: "Document Status",
      flex: 1,
      ...defaultColDef,
      ...leftAlignColDef,
      cellRenderer: StatusRenderer,
      sortable: false,
      suppressMovable: false,
    },
  ], []);

  const anySelected = selectedIds.size > 0;

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        multiple
        accept={ACCEPTED_TYPES}
        onChange={handleFileChange}
      />
      <Paper variant="Table Container">
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">{t("Evidence Repository")}</Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <AddButton
                  label="Upload Document"
                  disabled={!entityId}
                  onClick={() => fileInputRef.current?.click()}
                />
                <Tooltip title={t("Download Selected")}>
                  <span>
                    <IconButton
                      variant="Light Green"
                      disabled={!anySelected}
                      onClick={handleDownload}
                      sx={{ width: 40, height: 40 }}
                    >
                      <LuDownload style={{ fontSize: "18px" }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <RemoveButton
                  label="Delete Selected"
                  disabled={!anySelected}
                  onClick={handleDelete}
                />
              </Box>
              <Typography sx={{
                fontSize: "11.5px", color: "#94a3b8",
                fontFamily: "Poppins, sans-serif", fontWeight: 400,
                letterSpacing: "0.01em", lineHeight: 1,
              }}>
                Max 5MB &nbsp;·&nbsp; .pdf &nbsp;.csv &nbsp;.xlsx &nbsp;.doc &nbsp;.docx &nbsp;.jpg &nbsp;.png
              </Typography>
            </Box>
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box>
          <AgGrid
            rowData={rowData}
            columnDefs={columnDefs}
            headerCheckbox={false}
            pagination={true}
            paginationPageSize={10}
            onSelectionChanged={(e) => {
              const selected = e.api.getSelectedRows();
              setSelectedIds(new Set(selected.map((r) => r.id)));
            }}
            sx={{
              "& .ag-cell-wrapper .ag-selection-checkbox": {
                marginRight: "8px !important",
              },
            }}
          />
        </Box>
      </Paper>
    </>
  );
}

export default EvidenceRepository;
